import { Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { reportTreemapOptions } from '../highcharts-options';
import { QuickBuildService, UIFilter } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { departments, ReportVizChartColors } from '../constants';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SearchFilter } from 'ldt-people-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const FIELDS_TO_GROUP_BY = ['jobs.function', 'jobs.level'];
const REPORT_NAME = 'demographics';

@Component({
  selector: 'app-reports-functional-distribution',
  templateUrl: './functional-distribution.component.html',
  styleUrls: ['./functional-distribution.component.scss'],
  standalone: false,
})
export class FunctionalDistributionComponent extends BaseReportComponent {
  @Input() boxShadow: boolean = true;

  breakdownByOptions = FIELDS_TO_GROUP_BY;

  protected readonly CHART_ID = 'functional-distribution';
  protected readonly CHART_DATA_DESC =
    'Number of currentemployees broken out by their job function and level';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportTreemapOptions)
    );
    this.chart.update({ title: { text: 'Functional Distribution' } }, true, true);
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    if (!this.quickBuild) return;

    this.showLoading();

    const reportParams = {
      group_by: ['jobs.function', 'jobs.level'],
    };
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        this.quickBuild
      )
      .subscribe({
        next: (data) => {
          Log.d('FunctionalDistributionComponent data', data);
          const series = this.generateChartSeries(data.count, data.data.demographics);
          this.updateChart(series);
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('FunctionalDistributionComponent getChartData error', error);
          this.showChartError(error);
        },
      });
    this.subscriptions.add(sub);
  }

  protected generateChartSeries(total: number, data: any): any[] {
    Log.d('FunctionalDistributionComponent generateChartSeries', total, data);

    let points: any[] = [];
    let deptCounts = data.reduce(
      (r: any, i: any) => (
        (r[i.group_values[0].value] = (r[i.group_values[0].value] || 0) + i.count_employees), r
      ),
      {}
    );
    Object.keys(deptCounts).forEach((d: any) => {
      const keyIndex = this.departmentIndexer(d);
      const color = ReportVizChartColors[keyIndex];
      points.push({ id: 'id-' + keyIndex, name: d, value: deptCounts[d], color: color });
    });
    data.forEach((d: any, i: any) => {
      const dIndex = this.departmentIndexer(d.group_values[0].value);
      points.push({
        id: 'id-' + dIndex + '_' + i,
        name: d.group_values[1].value,
        value: d.count_employees,
        parent: 'id-' + dIndex,
      });
    });

    return [
      {
        name: 'Employees',
        type: 'treemap',
        layoutAlgorithm: 'squarified',
        allowDrillToNode: true,
        animationLimit: 1000,
        dataLabels: {
          enabled: false,
        },
        levels: [
          {
            level: 1,
            dataLabels: {
              enabled: true,
            },
            borderWidth: 3,
            levelIsConstant: false,
          },
          {
            level: 1,
            dataLabels: {
              style: {
                fontSize: '14px',
              },
            },
          },
        ],
        accessibility: {
          exposeAsGroupOnly: true,
        },
        data: points,
      },
    ];
  }

  departmentIndexer(dept: any) {
    return departments.indexOf(dept);
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    Log.d('FunctionalDistributionComponent getContextMenuItems', point);

    // For whatever reason highcharts doesn't seem to provide a type that corresponds to this runtime point for this chart type,
    // but isLeaf exists and is true only at the lowest level of the treemap, which is what we want
    if (!(point as any)?.node?.isLeaf) {
      return [];
    }

    const dept = (point as any).node.parentNode.name;
    const level = (point as any).node.name;

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    const deptFilter: UIFilter = {
      filter: {
        field: 'jobs.function',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [dept],
      },
    };
    const levelFilter: UIFilter = {
      filter: {
        field: 'jobs.level',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [level],
      },
    };

    return [
      {
        label: `View ${level} employees that work at ${dept}`,
        icon: 'group',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [deptFilter, levelFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          qbCopy.name = `${level} ${dept} employees`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }
}
